import React, { useState } from 'react';
import { Box, Container, Typography, Button, Paper, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GroupIcon from '@mui/icons-material/Group';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { Fade } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';

const brandBlue = '#0B60B1';

const steps = [
  {
    label: 'Profile Optimization',
    icon: <LinkedInIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We optimize your LinkedIn profile for maximum visibility and credibility.'
  },
  {
    label: 'Audience Targeting',
    icon: <GroupIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We identify and target your ideal audience for better engagement.'
  },
  {
    label: 'Content Strategy',
    icon: <CampaignIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We create and schedule compelling content to boost your brand.'
  },
  {
    label: 'Analytics & Growth',
    icon: <AnalyticsIcon color="primary" sx={{ fontSize: 32 }} />,
    desc: 'We monitor performance and refine strategies for continuous growth.'
  }
];

const faqs = [
  {
    q: 'How soon can I see results from LinkedIn marketing?',
    a: 'Most clients see increased engagement and connections within the first month, with lead generation ramping up in 2-3 months.'
  },
  {
    q: 'Do you manage company pages or personal profiles?',
    a: 'We can manage both, depending on your business goals and target audience.'
  },
  {
    q: 'What kind of content do you create?',
    a: 'We create posts, articles, and campaigns tailored to your brand and audience, including graphics and video.'
  },
  {
    q: 'Is LinkedIn marketing suitable for all industries?',
    a: 'LinkedIn is especially effective for B2B, professional services, and knowledge-based industries.'
  }
];

const services = [
  'Profile Optimization',
  'Content Creation',
  'Audience Targeting',
  'Lead Generation',
  'Campaign Management',
  'Analytics & Reporting',
  'Connection Growth',
  'Company Page Management',
  'Personal Branding',
  'B2B Outreach'
];

const marketingFactors = [
  {
    icon: <LinkedInIcon color="primary" sx={{ fontSize: 36 }} />,
    title: 'Profile Optimization',
    desc: 'Enhance your profile to attract the right audience and build trust.'
  },
  {
    icon: <GroupIcon color="secondary" sx={{ fontSize: 36 }} />,
    title: 'Audience Targeting',
    desc: 'Reach decision-makers and influencers in your industry.'
  },
  {
    icon: <CampaignIcon color="info" sx={{ fontSize: 36 }} />,
    title: 'Content Strategy',
    desc: 'Engage your network with high-impact content and campaigns.'
  },
  {
    icon: <AnalyticsIcon color="success" sx={{ fontSize: 36 }} />,
    title: 'Analytics & Growth',
    desc: 'Track performance and optimize for continuous improvement.'
  }
];

export default function LinkedinMarketingPage() {
  const [activeStep, setActiveStep] = useState(0);

  const funnelStats = [
    {
      title: 'Profile Optimization',
      stat: '3x Connections',
      desc: 'Clients see up to 3x more connection requests after profile optimization.'
    },
    {
      title: 'Audience Targeting',
      stat: '2x Engagement',
      desc: 'Targeted outreach doubles engagement rates on posts and messages.'
    },
    {
      title: 'Content Strategy',
      stat: '+150%',
      desc: 'Content campaigns boost profile views and inbound leads by 150%.'
    },
    {
      title: 'Analytics & Growth',
      stat: '4x ROI',
      desc: 'Clients typically achieve 4x return on investment within 6 months.'
    }
  ];

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', position: 'relative' }}>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', bgcolor: brandBlue, color: '#fff', py: { xs: 10, md: 14 }, overflow: 'hidden', minHeight: { md: 480 } }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={12}>
              <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                <Typography variant="h1" fontWeight={900} gutterBottom sx={{ letterSpacing: '-1.5px', mb: 3, fontSize: { xs: 36, md: 54 } }}>
                  LinkedIn Marketing That Delivers
                </Typography>
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 400, opacity: 0.95, maxWidth: 500, mx: 'auto' }}>
                  Unlock B2B growth with proven LinkedIn strategies for profile, content, and lead generation.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
                  <Button variant="contained" color="secondary" size="large" sx={{ fontWeight: 800, px: 5, py: 1.5, fontSize: 20, borderRadius: 3, boxShadow: 4, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>
                    Get Free Consultation
                  </Button>
                  <Button variant="outlined" color="inherit" size="large" sx={{ fontWeight: 700, borderColor: '#fff', px: 4, fontSize: 18, borderRadius: 3, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>
                    View Case Studies
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
          {/* Subtle animated SVG background */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
            <svg width="100%" height="100%" viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
              <path fill="#1976d2" fillOpacity="0.18" d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,133.3C840,107,960,85,1080,101.3C1200,117,1320,171,1380,197.3L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z" />
            </svg>
          </Box>
        </Container>
      </Box>

      {/* Features/Stepper Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h4" fontWeight={900} textAlign="center" gutterBottom sx={{ mb: 6, letterSpacing: '-0.5px' }}>
          How We Drive Results
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0, flexWrap: 'wrap', mb: 4 }}>
          {steps.map((step, idx) => (
            <React.Fragment key={step.label}>
              <Box
                onMouseEnter={() => setActiveStep(idx)}
                onFocus={() => setActiveStep(idx)}
                tabIndex={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mx: 1.5,
                  zIndex: 1,
                }}
              >
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: activeStep === idx ? 'primary.main' : '#e3eafc',
                  color: activeStep === idx ? '#fff' : 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                  boxShadow: activeStep === idx ? 6 : 1,
                  border: activeStep === idx ? '2.5px solid #094a8f' : '2.5px solid #e3eafc',
                  transition: 'all 0.2s',
                  mb: 1,
                }}>{step.icon}</Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ textAlign: 'center', fontSize: 16, mb: 0.5 }}>{step.label}</Typography>
              </Box>
              {idx < steps.length - 1 && (
                <Box sx={{ width: 40, height: 4, bgcolor: '#e3eafc', alignSelf: 'center', mx: 0.5, borderRadius: 2 }} />
              )}
            </React.Fragment>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 180 }}>
          <Paper elevation={6} sx={{ p: 5, borderRadius: 4, bgcolor: '#f7fafd', minWidth: 320, maxWidth: 420, textAlign: 'center', boxShadow: 3, transition: 'all 0.3s', mt: 2 }}>
            <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 2 }}>
              {funnelStats[activeStep].title}
            </Typography>
            <Typography variant="h2" fontWeight={900} color="secondary" sx={{ mb: 1, fontSize: { xs: 36, md: 54 } }}>
              {funnelStats[activeStep].stat}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 340, textAlign: 'center', mx: 'auto' }}>
              {funnelStats[activeStep].desc}
            </Typography>
          </Paper>
        </Box>
      </Container>

      {/* Statistics Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {[
            { label: 'Connections Grown', value: '5,000+' },
            { label: 'Leads Generated', value: '1,200+' },
            { label: 'Engagement Rate', value: '250%' },
            { label: 'ROI', value: '4x' }
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Our LinkedIn Marketing Services
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          End-to-end LinkedIn solutions for B2B growth and brand authority
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 4 }}>
          {services.map((service, index) => (
            <Chip
              key={service}
              label={service}
              color={index % 2 === 0 ? 'primary' : 'secondary'}
              variant={index % 3 === 0 ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: 16, px: 2, py: 2, height: 'auto', minHeight: 40, borderRadius: 2, boxShadow: 1 }}
            />
          ))}
        </Box>
      </Container>

      {/* Marketing Factors Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom color="primary">
          What Sets Us Apart
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Our proven approach to LinkedIn marketing delivers measurable results.
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {marketingFactors.map((factor, idx) => (
            <Grid item xs={12} sm={6} md={4} key={factor.title}>
              <Fade in timeout={600 + idx * 200}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-6px) scale(1.04)', boxShadow: 6 } }}>
                  <Box sx={{ mb: 2 }}>{factor.icon}</Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>{factor.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{factor.desc}</Typography>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Stack spacing={3}>
          {faqs.map((faq) => (
            <Accordion key={faq.q} TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: 8, textAlign: 'center', mt: 8, position: 'relative' }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <LinkedInIcon color="primary" sx={{ fontSize: 48, mb: 1, color: '#fff' }} />
            <Box sx={{ bgcolor: '#fff', color: 'primary.main', px: 3, py: 1, borderRadius: 99, fontWeight: 700, fontSize: 18, boxShadow: 2, mb: 2, display: 'inline-block' }}>
              100% Satisfaction Guarantee
            </Box>
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>Ready to Grow on LinkedIn?</Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'rgba(255,255,255,0.85)' }}>
            Let our experts build your brand and generate leads on LinkedIn.
          </Typography>
          <Button variant="contained" color="secondary" size="large" sx={{ fontWeight: 700, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.06)' } }}>Start Your LinkedIn Campaign</Button>
        </Container>
      </Box>
    </Box>
  );
} 